import { createFileRoute, Link } from "@tanstack/react-router";
import { hreflangLinks, articleMeta, absoluteUrl } from "@/lib/seo";
import { articleHeroFor } from "@/lib/article-heroes";
import { BlogPost, articleJsonLd, breadcrumbJsonLd } from "@/components/BlogPost";
import { KokeileTanaan, MiksiToimii, ValmisRatkaisu } from "@/components/hub/Callouts";

const SLUG = "montessori-lattiapatja";
const URL = `https://montessori24.fi/montessori-koulu/${SLUG}`;
const TITLE = "Montessori-lattiavuode – turvallisuus ja käytännön vinkit | Montessori24";
const DESC =
  "Mitä Montessori-lattiavuoteesta kannattaa tietää? Tutustu ajatukseen, huoneen lapsiturvallisuuteen ja siihen, miten Montessori-periaatteet eroavat vauvan turvallisen unen suosituksista.";
const PUBLISHED = "2026-07-04";
const HERO = articleHeroFor(SLUG);
const UPDATED = "2026-08-16";

export const Route = createFileRoute("/montessori-koulu/montessori-lattiapatja")({
  head: () => ({
    meta: articleMeta({
      slug: SLUG,
      title: TITLE,
      description: DESC,
      published: PUBLISHED,
      updated: UPDATED,
      image: HERO,
    }),
    links: [{ rel: "canonical", href: URL }, ...hreflangLinks(URL)],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(articleJsonLd({ title: TITLE, description: DESC, url: URL, image: absoluteUrl(HERO), publishedISO: PUBLISHED })) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd(SLUG, TITLE)) },
    ],
  }),
  component: Post,
});

function Post() {
  return (
    <BlogPost
      eyebrow="Uni & huone"
      title="Montessori-lattiavuode – mitä vanhemman kannattaa tietää?"
      lead="Montessori-lattiavuoteella tarkoitetaan matalaa nukkumapaikkaa, josta liikkuva lapsi pystyy myöhemmin nousemaan itse ylös ja palaamaan takaisin. Ajatus liittyy Montessori-ympäristön saavutettavuuteen ja lapsen itsenäisyyteen."
      readingMinutes={7}
      publishedISO={PUBLISHED}
      updatedISO={UPDATED}
    >
      <MiksiToimii>
        <p>
          Vauvan turvallinen uni on eri asia kuin Montessori-pedagogiikka. Pienen vauvan nukkumisjärjestelyissä tulee
          noudattaa ajantasaisia turvallisen unen suosituksia sekä nukkumapaikan valmistajan ohjeita.
        </p>
      </MiksiToimii>

      <h2>1. Mitä Montessori-lattiavuoteella tarkoitetaan?</h2>
      <p>
        Lattiavuode on matala nukkumapaikka, joka on osa saavutettavaa ympäristöä: lapsi näkee huoneensa omalta
        korkeudeltaan ja voi kehitysvaiheessaan mennä sänkyyn ja nousta siitä itse. Ajatus on pedagoginen — se koskee
        ympäristön saavutettavuutta ja itsenäisyyttä, ei nukkumisen turvallisuutta.
      </p>
      <p>Lattiavuodetta ei siis kannata ajatella lääketieteellisesti parempana ratkaisuna, vaan yhtenä tapana järjestää lapsen huone.</p>

      <h2>2. Lattiavuode ei ole automaattisesti vauvan turvallisin nukkumapaikka</h2>
      <p>
        Tavallinen lattialle asetettu patja ei automaattisesti vastaa vauvoille suunnitellun pinnasängyn, kehdon tai
        muun hyväksytyn nukkumapaikan turvallisuusominaisuuksia.
      </p>
      <p>
        Vauvan unta koskeva ohjeistus korostaa yleisesti sitä, että nukkumapaikka on nimenomaan vauvalle suunniteltu ja
        sitä käytetään valmistajan ohjeiden mukaisesti. Vauvan nukkumisympäristössä turvallisuus on tärkeämpää kuin
        Montessori-periaatteiden soveltaminen. Pelkkä se, että vauva oppii kääntymään tai ryömimään, ei ole peruste
        vaihtaa nukkumapaikkaa.
      </p>

      <h2>3. Milloin lattiavuodetta voi harkita?</h2>
      <p>
        Ajankohta riippuu lapsen kehityksestä, nukkumisjärjestelystä, käytettävästä tuotteesta ja paikallisista
        turvallisen unen suosituksista. Yleispätevää ikää ei kannata olettaa.
      </p>
      <p>
        Osa perheistä siirtyy matalaan sänkyyn siinä vaiheessa, kun vauva-ajan erityiset nukkumisjärjestelyt eivät ole
        enää tarpeen ja lapsi liikkuu huoneessa varmasti. Jos olet epävarma, kysy neuvolasta.
      </p>

      <h2>4. Huoneesta tulee osa nukkumisympäristöä</h2>
      <p>
        Kun lapsi voi poistua sängystä itse, koko huone on käytännössä osa nukkumisympäristöä. Käy huone läpi lapsen
        korkeudelta ja huolehdi ainakin näistä:</p>
      <ul>
        <li>kaatuvat kalusteet kiinnitetään seinään tarvittaessa</li>
        <li>pistorasiat suojataan tai niiden ulottuville pääsy estetään</li>
        <li>johdot siirretään pois lapsen ulottuvilta</li>
        <li>pienet, suuhun mahtuvat esineet pois lattiatasolta</li>
        <li>ikkunat pysyvät kiinni tai turvalukittuina</li>
        <li>portaisiin ei pääse — turvaportti tarvittaessa</li>
        <li>epävakaat kalusteet pois huoneesta</li>
        <li>lämmittimiin ja lämpöpattereihin ei pääse käsiksi</li>
        <li>verhojen ja kaihtimien narut kiinnitetään ylös</li>
        <li>oven käyttö mietitään etukäteen: sormisuoja ja se, pääseekö lapsi muihin tiloihin</li>
      </ul>

      <h2>5. Patjan ja sängyn rakenne</h2>
      <ul>
        <li>Noudata sängyn ja patjan valmistajan ohjeita.</li>
        <li>Käytä patjaa, joka on tarkoitettu kyseiseen sänkyyn tai runkoon.</li>
        <li>Patjan tulee sopia paikalleen ilman vaarallisia rakoja reunojen ja rungon välissä.</li>
        <li>Huolehdi ilmankierrosta, jos valmistaja sitä edellyttää.</li>
        <li>Tarkista patja ja runko säännöllisesti vaurioiden varalta.</li>
      </ul>
      <p>
        Vauvalle tarkoitettu patja ja isomman lapsen patja ovat eri tuotteita: vauvan nukkumapaikkaan valitaan aina
        nimenomaan siihen tarkoitettu patja valmistajan ohjeen mukaan.
      </p>

      <h2>6. Lattiavuode ei takaa parempaa unta</h2>
      <p>
        Lattiavuode ei itsessään takaa nopeampaa nukahtamista, vähäisempiä yöheräämisiä tai parempaa unen laatua.
        Lasten uni vaihtelee yksilöllisesti, ja siihen vaikuttavat monet asiat sängyn mallia enemmän.
      </p>

      <h2>7. Entä itsenäisyys?</h2>
      <p>
        Kun lapsi on kehitysvaiheessa, jossa matala sänky on hänelle turvallinen ja sopiva, se voi antaa enemmän
        mahdollisuuksia nousta sänkyyn ja pois sängystä itsenäisesti. Se voi myös tehdä iltarutiinista rauhallisemman,
        kun lapsi voi hakea kirjan hyllystä ja palata sänkyyn itse.
      </p>

      <h2>8. Milloin pinnasänky voi olla parempi valinta?</h2>
      <ul>
        <li>lapsi on vielä vaiheessa, jossa noudatetaan vauvan turvallisen unen suosituksia</li>
        <li>huonetta ei saada kokonaan lapsiturvalliseksi</li>
        <li>lapsi poistuu sängystä toistuvasti tavalla, joka ei ole turvallista</li>
        <li>valmistajan ikä- tai painosuositukset puoltavat toista nukkumaratkaisua</li>
        <li>perhe kokee pinnasängyn rajaavat ominaisuudet itselleen sopivammiksi</li>
      </ul>
      <p>Kumpikaan valinta ei tee vanhemmuudesta parempaa tai huonompaa — ratkaisu tehdään lapsen, kodin ja ohjeiden ehdoilla.</p>

      <KokeileTanaan>
        <p>
          Jos harkitset myöhemmin lattiavuodetta, tarkastele koko huonetta lapsen korkeudelta. Mitä lapsi voi vetää
          alas, kiivetä, avata tai laittaa suuhunsa?
        </p>
      </KokeileTanaan>

      <h2>Lähteet ja lisätietoa</h2>
      <ul>
        <li>Terveyden ja hyvinvoinnin laitos (THL) ja neuvolan ohjeet: vauvan turvallinen uni ja nukkumisympäristö.</li>
        <li>Turvallisuus- ja kemikaalivirasto (Tukes): kodin ja lastenhuoneen turvallisuus.</li>
        <li>Nukkumapaikan ja patjan valmistajan käyttöohje — ensisijainen lähde tuotekohtaisissa kysymyksissä.</li>
        <li>Montessori, M. (1949). The Absorbent Mind — pedagoginen tausta, ei turvallisuusohje.</li>
      </ul>

      <h2>Lue myös</h2>
      <p>
        Huoneen kokonaisuuteen saat ideoita artikkeleista{" "}
        <Link to="/montessori-koulu/montessori-kalusteet-kotiin">Montessori-kalusteet kotiin</Link> ja{" "}
        <Link to="/montessori-koulu/montessori-kotona">Montessori kotona — valmisteltu ympäristö</Link>.
      </p>

      <ValmisRatkaisu
        intro="Jos matala sänky on lapsellesi ajankohtainen, vertaile malleja ja lue tuotekohtaiset tiedot sekä valmistajan ohjeet."
        picks={[
          { to: "/montessori-sanky", label: "Montessori-sängyt", note: "Matalat sängyt ja tuotekohtaiset tiedot." },
          { to: "/lastenhuoneen-kalusteet", label: "Lastenhuoneen kalusteet", note: "Hyllyt ja säilytys lapsen korkeudelle." },
        ]}
      />
    </BlogPost>
  );
}
